
import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { PlusCircle, Loader2, Building2, Wallet, Search, Edit, Trash2, Download } from "lucide-react";
import { feesApportionPartyApi } from "@/services/api";
import { financialAccountApi } from "@/services/api/financialAccountApi";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FeesApportionParty } from "@/models";
import { Textarea } from "@/components/ui/textarea";
import { useAppSettings } from "@/hooks/useAppSettings";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const FinanceSettings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const platformBankDetailsRef = useRef<HTMLDivElement>(null);
  const {
    settings,
    isLoading: settingsLoading,
    saveSettings
  } = useAppSettings();
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "",
    wireInstructions: "",
    financeNotes: "",
    billingAddress: "",
    billingCity: "",
    billingState: "",
    billingZip: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");

  // Fetch fees apportion parties
  const {
    data: apportionmentParties,
    isLoading: isLoadingParties
  } = useQuery({
    queryKey: ["feesApportionParties"],
    queryFn: () => feesApportionPartyApi.list()
  });

  // Fetch financial accounts with payout details
  const {
    data: financialAccounts,
    isLoading: isLoadingAccounts,
    refetch: refetchAccounts
  } = useQuery({
    queryKey: ["financialAccounts"],
    queryFn: () => financialAccountApi.getAccounts({ include_balances: false })
  });

  // Initialize form with settings when they load
  useEffect(() => {
    if (settings) {
      setBankDetails({
        accountHolderName: settings.accountHolderName || "",
        bankName: settings.bankName || "",
        accountNumber: settings.accountNumber || "",
        routingNumber: settings.routingNumber || "",
        accountType: settings.accountType || "",
        wireInstructions: settings.wireInstructions || "",
        financeNotes: settings.financeNotes || "",
        billingAddress: settings.billingAddress || "",
        billingCity: settings.billingCity || "",
        billingState: settings.billingState || "",
        billingZip: settings.billingZip || ""
      });
    }
  }, [settings]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSaveBankDetails = async () => {
    setIsSaving(true);
    try {
      // Save to platform_settings via the hook
      await saveSettings({
        ...settings,
        accountHolderName: bankDetails.accountHolderName,
        bankName: bankDetails.bankName,
        accountNumber: bankDetails.accountNumber,
        routingNumber: bankDetails.routingNumber,
        accountType: bankDetails.accountType,
        wireInstructions: bankDetails.wireInstructions,
        financeNotes: bankDetails.financeNotes,
        billingAddress: bankDetails.billingAddress,
        billingCity: bankDetails.billingCity,
        billingState: bankDetails.billingState,
        billingZip: bankDetails.billingZip
      });

      // Also update the platform's payout_account for withdrawal validation
      const platformAccount = financialAccounts?.find((acc: any) => acc.account_type === 'platform');
      if (platformAccount) {
        try {
          const payoutData = {
            payout_method: 'bank_account' as const,
            account_holder_name: bankDetails.accountHolderName || 'Platform',
            bank_name: bankDetails.bankName || '',
            bank_account_number: bankDetails.accountNumber || '',
            bank_routing_number: bankDetails.routingNumber || '',
            bank_account_type: bankDetails.accountType || 'checking',
            street_address: bankDetails.billingAddress || '',
            city: bankDetails.billingCity || '',
            state_province: bankDetails.billingState || '',
            postcode: bankDetails.billingZip || '',
            is_default: true,
          };
          await financialAccountApi.createPayoutAccount(platformAccount.id, payoutData);
        } catch (payoutError) {
          console.error('Error updating platform payout account:', payoutError);
          // Don't throw - the main settings were saved successfully
        }
      }

      toast.success("Platform bank details saved successfully");
      refetchAccounts(); // Refresh the accounts list
    } finally {
      setIsSaving(false);
    }
  };

  // Delete account mutation
  const deleteMutation = useMutation({
    mutationFn: (accountId: number) => financialAccountApi.deleteAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financialAccounts"] });
      toast.success("Account deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete account");
    },
  });

  // Helper function to get account type label
  const getAccountTypeLabel = (accountType: string) => {
    switch (accountType) {
      case "platform":
        return "Platform";
      case "association":
        return "Association";
      case "management_company":
        return "Management Company";
      case "payment_gateway":
        return "Payment Gateway";
      default:
        return accountType;
    }
  };

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!financialAccounts) return [];
    if (!accountSearch.trim()) return financialAccounts;

    const searchLower = accountSearch.toLowerCase();
    return financialAccounts.filter((account: any) => {
      const typeLabel = getAccountTypeLabel(account.account_type).toLowerCase();
      const reference = account.reference?.toLowerCase() || "";
      const displayName = account.display_name?.toLowerCase() || "";
      const bankName = account.bank_name?.toLowerCase() || "";
      const accountNumber = account.account_number?.toLowerCase() || "";

      return (
        typeLabel.includes(searchLower) ||
        reference.includes(searchLower) ||
        displayName.includes(searchLower) ||
        bankName.includes(searchLower) ||
        accountNumber.includes(searchLower)
      );
    });
  }, [financialAccounts, accountSearch]);

  const handleRowClick = (partyId: number) => {
    navigate(`/backoffice/fees-apportion-parties/${partyId}`);
  };

  const handleDeleteAccount = (accountId: number, accountType: string) => {
    if (confirm(`Are you sure you want to delete this ${accountType} account? This action cannot be undone.`)) {
      deleteMutation.mutate(accountId);
    }
  };

  const handleEditAccount = (account: any) => {
    // Navigate to appropriate edit page based on account type
    if (account.account_type === 'management_company' && account.reference) {
      navigate(`/backoffice/management-companies/${account.reference}/edit`);
    } else if (account.account_type === 'association' && account.reference) {
      navigate(`/backoffice/associations/${account.reference}/edit`);
    } else if (account.account_type === 'platform') {
      // Scroll to Platform Bank Details section
      platformBankDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (account.account_type === 'payment_gateway') {
      toast.info("Payment gateway accounts are managed externally (e.g., Stripe dashboard)");
    } else {
      toast.error("Unable to edit this account - no reference ID found");
    }
  };

  const handleAddAccount = () => {
    // TODO: Navigate to add page or open modal
    toast.info("Add account functionality coming soon");
  };

  const handleExportAccounts = () => {
    if (!financialAccounts || financialAccounts.length === 0) {
      toast.error("No accounts to export");
      return;
    }

    // Create CSV content with all bank details
    const headers = [
      "Account Type",
      "Reference",
      "Display Name",
      "Bank Name",
      "Account Number",
      "Routing Number",
      "Account Holder Name",
      "Account Type (Bank)",
      "Status",
      "Street Address",
      "City",
      "State",
      "Zip Code",
    ];

    const rows = financialAccounts.map((account: any) => [
      getAccountTypeLabel(account.account_type),
      account.reference || "",
      account.display_name || "",
      account.bank_name || "",
      account.account_number || "",
      account.routing_number || "",
      account.account_holder_name || "",
      account.bank_account_type || "",
      account.status || "",
      account.street_address || "",
      account.city || "",
      account.state_province || "",
      account.postcode || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `financial-accounts-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Financial accounts exported successfully");
  };

  const maskAccountNumber = (accountNumber: string | null | undefined) => {
    if (!accountNumber) return "-";
    if (accountNumber.length <= 4) return accountNumber;
    return "****" + accountNumber.slice(-4);
  };

  const getAccountTypeBadgeVariant = (accountType: string) => {
    switch (accountType) {
      case "platform":
        return "default";
      case "association":
        return "secondary";
      case "management_company":
        return "outline";
      case "payment_gateway":
        return "destructive";
      default:
        return "outline";
    }
  };

  return <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Financial Accounts
              </CardTitle>
              <CardDescription>
                Manage all financial accounts and their bank account details
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportAccounts}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
              <Button onClick={handleAddAccount}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Account
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search accounts by type, reference, bank name, or account number..."
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {isLoadingAccounts ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredAccounts && filteredAccounts.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Bank Name</TableHead>
                    <TableHead>Account Number</TableHead>
                    <TableHead>Routing Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account: any) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Badge variant={getAccountTypeBadgeVariant(account.account_type)}>
                          {getAccountTypeLabel(account.account_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {account.display_name || account.reference ? (
                          <span className="text-sm">{account.display_name || account.reference}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {account.bank_name ? (
                          <div className="flex items-center gap-2">
                            <Wallet className="h-4 w-4 text-muted-foreground" />
                            {account.bank_name}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {account.account_number ? (
                          <span className="font-mono text-sm">
                            {account.account_number}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {account.routing_number ? (
                          <span className="font-mono text-sm">
                            {account.routing_number}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={account.status === "active" ? "success" : "secondary"}>
                          {account.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAccount(account)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAccount(account.id, getAccountTypeLabel(account.account_type))}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : accountSearch.trim() ? (
            <div className="text-center p-8 text-muted-foreground">
              No accounts match your search criteria
            </div>
          ) : (
            <div className="text-center p-8 text-muted-foreground">
              No financial accounts found
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Fee Apportionment Parties</CardTitle>
          <CardDescription>
            Manage the entities that can receive portions of fees.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-4">
            <Button asChild>
              <Link to="/backoffice/fees-apportion-parties/new">
                <PlusCircle className="mr-2 h-4 w-4" />
                New Apportionment Party
              </Link>
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entity Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingParties ? <TableRow>
                  <TableCell colSpan={3} className="text-center">Loading...</TableCell>
                </TableRow> : apportionmentParties?.length === 0 ? <TableRow>
                  <TableCell colSpan={3} className="text-center">No apportionment parties found</TableCell>
                </TableRow> : apportionmentParties?.map((party: FeesApportionParty) => <TableRow key={party.id} className="cursor-pointer" onClick={() => handleRowClick(party.id)}>
                    <TableCell>{party.entity_name}</TableCell>
                    <TableCell>{party.description || "-"}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/backoffice/fees-apportion-parties/${party.id}`}>
                          Edit
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card ref={platformBankDetailsRef}>
        <CardHeader>
          <CardTitle>Platform Bank Details</CardTitle>
          <CardDescription>Manage bank account information for the platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? <div className="flex items-center justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div> : <>
              <div className="space-y-2">
                <Label htmlFor="accountHolderName">Account Holder Name</Label>
                <Input id="accountHolderName" value={bankDetails.accountHolderName} onChange={handleInputChange} placeholder="Enter account holder name" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input id="bankName" value={bankDetails.bankName} onChange={handleInputChange} placeholder="Enter bank name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Input id="accountNumber" value={bankDetails.accountNumber} onChange={handleInputChange} placeholder="Enter account number" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="routingNumber">Routing Number</Label>
                  <Input id="routingNumber" value={bankDetails.routingNumber} onChange={handleInputChange} placeholder="Enter routing number" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountType">Account Type</Label>
                  <Input id="accountType" value={bankDetails.accountType} onChange={handleInputChange} placeholder="Enter account type" />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-2">
                <Label htmlFor="billingAddress">Billing Address</Label>
                <Input id="billingAddress" value={bankDetails.billingAddress} onChange={handleInputChange} placeholder="Enter street address" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billingCity">City</Label>
                  <Input id="billingCity" value={bankDetails.billingCity} onChange={handleInputChange} placeholder="Enter city" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billingState">State</Label>
                  <Input id="billingState" value={bankDetails.billingState} onChange={handleInputChange} placeholder="Enter state" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billingZip">Zip Code</Label>
                  <Input id="billingZip" value={bankDetails.billingZip} onChange={handleInputChange} placeholder="Enter zip code" />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-2">
                <Label htmlFor="wireInstructions">Wire Instructions</Label>
                <Textarea id="wireInstructions" value={bankDetails.wireInstructions} onChange={handleInputChange} placeholder="Enter wire transfer instructions" rows={3} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="financeNotes">Additional Notes</Label>
                <Textarea id="financeNotes" value={bankDetails.financeNotes} onChange={handleInputChange} placeholder="Enter any additional notes about banking" rows={3} />
              </div>

              <Button onClick={handleSaveBankDetails} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Platform Bank Details
              </Button>
            </>}
        </CardContent>
      </Card>
    </div>;
};

export default FinanceSettings;
